import { CurrencyPipe, DatePipe, DecimalPipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { Currency, DailyAdSpendFee, KeywordTrackerQuota, Organization } from "@front/m19-api-client";
import { AuthService, KeywordTrackingService, StatsService } from "@front/m19-services";
import {
  IButtonDirective,
  ICardComponent,
  ICardHeader,
  IIconComponent,
  IProgressBarComponent,
  ITooltipDirective,
  ModalService,
} from "@front/m19-ui";
import { DateUtils } from "@front/m19-utils";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { DateTime } from "luxon";
import { catchError, of, switchMap } from "rxjs";
import {
  BillableAdSpendDetailModalComponent,
  BillableAdSpendDetailModalData,
} from "../billable-ad-spend-detail-modal/billable-ad-spend-detail-modal.component";
import {
  TrackedKeywordsDetailModalComponent,
  TrackedKeywordsDetailModalData,
} from "../tracked-keywords-detail-modal/tracked-keywords-detail-modal.component";

export interface KeywordTrackerGauge {
  used?: number;
  limit?: number;
}

@Component({
  selector: "app-billable-ad-spend-card",
  templateUrl: "./billable-ad-spend-card.component.html",
  imports: [
    TranslocoDirective,
    CurrencyPipe,
    DatePipe,
    DecimalPipe,
    ICardComponent,
    ICardHeader,
    IButtonDirective,
    IIconComponent,
    ITooltipDirective,
    IProgressBarComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BillableAdSpendCardComponent {
  private readonly statsService = inject(StatsService);
  private readonly keywordTrackingService = inject(KeywordTrackingService);
  private readonly modalService = inject(ModalService);
  private readonly translocoService = inject(TranslocoService);
  private readonly authService = inject(AuthService);

  readonly organization = input.required<Organization>();
  readonly nextInvoiceDate = input<string>();

  readonly locale = toSignal(this.authService.userLocale$, { requireSync: true });
  private readonly dailyAdSpendFees = toSignal(this.statsService.getDailyAdSpendFee());
  private readonly dailyDspAdSpendFees = toSignal(this.statsService.getDailyDspAdSpendFee());
  private readonly dailyDspM19SeatAdSpendFees = toSignal(this.statsService.getDailyDspM19SeatAdSpendFee());

  private readonly unbilledFeeLists = computed(() => {
    const adSpendFees = this.dailyAdSpendFees();
    const dspAdSpendFees = this.dailyDspAdSpendFees();
    const dspM19SeatAdSpendFees = this.dailyDspM19SeatAdSpendFees();
    if (!adSpendFees || !dspAdSpendFees || !dspM19SeatAdSpendFees) return undefined;
    return {
      adSpendFees: this.unbilledFees(adSpendFees),
      dspAdSpendFees: this.unbilledFees(dspAdSpendFees),
      dspM19SeatAdSpendFees: this.unbilledFees(dspM19SeatAdSpendFees),
    };
  });

  readonly adSpend = computed(() => {
    const feeLists = this.unbilledFeeLists();
    if (!feeLists) return undefined;
    return [...feeLists.adSpendFees, ...feeLists.dspAdSpendFees, ...feeLists.dspM19SeatAdSpendFees].reduce(
      (total, fee) => total + (fee.adSpendFee ?? 0),
      0,
    );
  });

  private readonly keywordQuotas = toSignal(
    toObservable(this.organization).pipe(
      switchMap((organization) =>
        this.keywordTrackingService
          .getDailyHourlyKeywordNumber(organization.organizationId)
          .pipe(catchError(() => of(null))),
      ),
    ),
  );

  readonly keywordTrackerGauge = computed<KeywordTrackerGauge | undefined>(() => {
    const quotas = this.keywordQuotas();
    if (quotas === null) return undefined;
    const billingPlan = this.organization().billingPlan;
    const dailyLimit = billingPlan?.kwTrackingDailyLimit;
    const hourlyLimit = billingPlan?.kwTrackingHourlyLimit;
    return {
      used: quotas?.reduce((total: number, quota: KeywordTrackerQuota) => total + (quota.keywordNumber ?? 0), 0),
      limit: dailyLimit !== undefined && hourlyLimit !== undefined ? dailyLimit + hourlyLimit : undefined,
    };
  });

  readonly currency = computed(() => this.organization().billingPlan?.currency ?? Currency.EUR);

  private unbilledFees(fees: DailyAdSpendFee[]): DailyAdSpendFee[] {
    const organizationId = this.organization().organizationId;
    const oneMonthAgo = DateTime.local().toUTC().minus({ month: 1 });
    return fees.filter(
      (x) =>
        (x.parentOrganizationId ?? x.organizationId) === organizationId &&
        !x.stripeInvoiceId &&
        DateUtils.toDateTime(x.date!) > oneMonthAgo,
    );
  }

  openSpendByAccount(): void {
    const feeLists = this.unbilledFeeLists();
    if (!feeLists) return;
    this.modalService.openModal<BillableAdSpendDetailModalData, void>(BillableAdSpendDetailModalComponent, {
      modalTitle: this.translocoService.translate("billable-ad-spend-detail-modal.title"),
      size: "lg",
      data: {
        billingPlan: this.organization().billingPlan,
        currency: this.currency(),
        exportFilePrefix: "billable_ad_spend",
        locale: this.locale(),
        ...feeLists,
      },
    });
  }

  openTrackedKeywordsByAccount(): void {
    const billingPlan = this.organization().billingPlan;
    if (!billingPlan) return;
    const keywordQuotas = this.keywordQuotas();
    if (!keywordQuotas) return;
    this.modalService.openModal<TrackedKeywordsDetailModalData, void>(TrackedKeywordsDetailModalComponent, {
      modalTitle: this.translocoService.translate("billable-ad-spend-card.tracked_keywords_detail"),
      size: "lg",
      data: {
        organizationId: this.organization().organizationId,
        billingPlan,
        locale: this.locale(),
        keywordQuotas,
      },
    });
  }
}
